"use client";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import CompactRideList from "./CompactRideList";
import styles from "./HomepagePassengerRadar.module.css";
import { parseSavedDriverVehicle, type SavedDriverVehicle } from "@/lib/savedDriverVehicle";
import { createAvailabilityQueue, isRadarPoint, liveRadarRequests, radarAcceptPayload, validateRadarVehicle,
  type RadarPoint, type RadarRequest, type RadarVehicle } from "@/lib/homepagePassengerRadar";

type Props = { map: LeafletMap | null; onClose: () => void };
class RadarApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}
async function readApi(url: string, options: RequestInit = {}) {
  const response = await fetch(url, { cache: "no-store", ...options });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new RadarApiError(data?.message || "Unable to update radar. Please refresh status.", response.status);
  if (!data) throw new Error("Unable to read server response. Refresh status before trying again.");
  return data;
}
async function acceptedRequests(signal?: AbortSignal): Promise<RadarRequest[]> {
  const data = await readApi("/api/ride-requests?mode=ride-now-accepted", { signal });
  if (!Array.isArray(data)) throw new Error("Unable to read accepted requests.");
  return data;
}
// Shared across panel mounts, so a previous heartbeat cannot overtake a new Offline write.
const enqueueAvailability = createAvailabilityQueue();
const availabilityPatch = (body: object) => readApi("/api/driver-availability", {
  method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
});

export default function HomepagePassengerRadar({ map, onClose }: Props) {
  const [ready, setReady] = useState(false);
  const [online, setOnline] = useState(false);
  const [location, setLocation] = useState<RadarPoint | null>(null);
  const [vehicle, setVehicle] = useState<RadarVehicle>({ type: "Any", name: "", number: "", radius: "25" });
  const [vehicleSaved, setVehicleSaved] = useState(false);
  const [vehicleLoaded, setVehicleLoaded] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(true);
  const [vehicleDraft, setVehicleDraft] = useState<SavedDriverVehicle>({ type: "Any", name: "", number: "" });
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [vehicleError, setVehicleError] = useState("");
  const [vehicleRefresh, setVehicleRefresh] = useState(0);
  const saveVehicleLock = useRef(false);
  const [requests, setRequests] = useState<RadarRequest[]>([]);
  const [accepted, setAccepted] = useState<RadarRequest[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [price, setPrice] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [message, setMessage] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [recenter, setRecenter] = useState(0);
  const alive = useRef(false);
  const tracking = useRef(false);
  const locationRef = useRef<RadarPoint | null>(null);
  const toggleLock = useRef(false);
  const actionLock = useRef(false);
  const actionVersion = useRef(0);
  const lastHeartbeat = useRef(0);
  // Kept in component memory, never in browser localStorage.
  const availabilitySessionToken = useRef<string | null>(null);
  const lastView = useRef("");
  const selected = requests.find(item => item.id === selectedId);
  const active = accepted[0];
  const hasLocation = location !== null;

  function report(cause: unknown) {
    if (!alive.current) return;
    setMessage(cause instanceof Error ? cause.message : "Unable to load radar.");
    if (cause instanceof RadarApiError && [401, 403].includes(cause.status)) {
      tracking.current = false; setNeedsLogin(cause.status === 401); setReady(false);
    }
  }

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; tracking.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      try {
        await enqueueAvailability(async () => undefined);
        if (controller.signal.aborted) return;
        const [availability, current] = await Promise.all([
          readApi("/api/driver-availability", { signal: controller.signal }), acceptedRequests(controller.signal),
        ]);
        if (controller.signal.aborted) return;
        // GET reports availability status, never an owned session token.
        // A reload or refresh must not resume GPS heartbeats without ownership.
        const ownedSession = Boolean(availabilitySessionToken.current);
        const canResume = availability.driverAvailableNow === true && ownedSession;
        setOnline(canResume); setAccepted(current);
        locationRef.current = null; setLocation(null); setRequests([]); setSelectedId(null);
        // Saved/manual map positions never substitute for fresh driver GPS.
        setReady(true); setNeedsLogin(false); setUncertain(false);
        setMessage(
          availability.driverAvailableNow === true && !ownedSession
            ? "Previous Online session detected. Press Go Online to start a fresh GPS session."
            : ""
        );
      } catch (cause) { if (!controller.signal.aborted) report(cause); }
    }
    void load();
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadVehicle() {
      try {
        const data = await readApi("/api/driver-vehicle", { signal: controller.signal });
        const saved = data.vehicle === null ? null : parseSavedDriverVehicle(data.vehicle);
        if (controller.signal.aborted) return;
        if (saved) {
          setVehicle(current => ({ ...current, ...saved })); setVehicleDraft(saved);
        }
        setVehicleSaved(saved !== null); setEditingVehicle(saved === null);
        setVehicleLoaded(true); setVehicleError("");
      } catch (cause) {
        if (!controller.signal.aborted) setVehicleError(cause instanceof Error ? cause.message : "Unable to load saved vehicle.");
      }
    }
    void loadVehicle();
    return () => controller.abort();
  }, [vehicleRefresh]);

  async function saveVehicle() {
    if (saveVehicleLock.current || toggleLock.current || actionLock.current || !vehicleLoaded) return;
    let draft: SavedDriverVehicle;
    try { draft = parseSavedDriverVehicle(vehicleDraft); }
    catch (cause) { setVehicleError(cause instanceof Error ? cause.message : "Invalid vehicle."); return; }
    saveVehicleLock.current = true; setSavingVehicle(true); setVehicleError("");
    try {
      const data = await readApi("/api/driver-vehicle", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const saved = parseSavedDriverVehicle(data.vehicle);
      if (!alive.current) return;
      setVehicle(current => ({ ...current, ...saved })); setVehicleDraft(saved);
      setVehicleSaved(true); setEditingVehicle(false);
    } catch (cause) {
      if (alive.current) setVehicleError(cause instanceof Error ? cause.message : "Unable to save vehicle.");
    } finally { saveVehicleLock.current = false; if (alive.current) setSavingVehicle(false); }
  }

  useEffect(() => {
    if (!ready || !online || busy) return;
    if (!navigator.geolocation) return;
    tracking.current = true;
    const watchId = navigator.geolocation.watchPosition(position => {
      if (!tracking.current || !alive.current) return;
      const point = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      if (!isRadarPoint(point)) return;
      locationRef.current = point; setLocation(point);
      if (Date.now() - lastHeartbeat.current < 10000) return;
      lastHeartbeat.current = Date.now();
      void enqueueAvailability(async () => {
        if (!tracking.current || !alive.current) return;
        if (!availabilitySessionToken.current) return;
        await availabilityPatch({
          available: true,
          action: "heartbeat",
          availabilitySessionToken: availabilitySessionToken.current,
          ...point,
        });
      }).catch(report);
    }, () => {
      if (!alive.current || !tracking.current) return;
      // Hide stale search pins until another valid GPS fix arrives.
      locationRef.current = null; setLocation(null);
      if (alive.current) setMessage("GPS unavailable. Allow location access to keep the radar current.");
    }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });
    return () => { tracking.current = false; navigator.geolocation.clearWatch(watchId); };
  }, [ready, online, busy]);

  // Poll sequentially: no overlapping responses or hidden retries of mutations.
  useEffect(() => {
    if (!ready || acting || busy) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        if (actionLock.current) return;
        const version = actionVersion.current;
        const current = await acceptedRequests(controller.signal);
        if (controller.signal.aborted || version !== actionVersion.current) return;
        setAccepted(current);
        const point = locationRef.current;
        if (!online || !isRadarPoint(point) || current.length) { setRequests([]); return; }
        const radius = Number(vehicle.radius);
        if (!Number.isFinite(radius) || radius < 1 || radius > 200) { setRequests([]); return; }
        const params = new URLSearchParams({ driverLat: String(point.latitude), driverLng: String(point.longitude), radiusKm: String(radius) });
        if (vehicle.type !== "Any") params.set("vehicleType", vehicle.type);
        const data = await readApi(`/api/ride-requests?${params}`, { signal: controller.signal });
        if (!Array.isArray(data)) throw new Error("Unable to read passenger requests.");
        if (controller.signal.aborted || version !== actionVersion.current) return;
        const live = liveRadarRequests(data); setRequests(live);
        setSelectedId(id => live.some(item => item.id === id) ? id : null);
      } catch (cause) { if (!controller.signal.aborted) { setRequests([]); report(cause); } }
      finally { if (!controller.signal.aborted) timer = setTimeout(() => { void poll(); }, 5000); }
    }
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [ready, online, vehicle.radius, vehicle.type, acting, busy, hasLocation]);

  useEffect(() => {
    if (!map) return;
    let cancelled = false;
    let layer: import("leaflet").LayerGroup | null = null;
    void import("leaflet").then(L => {
      if (cancelled) return;
      layer = L.layerGroup().addTo(map);
      const point = isRadarPoint(location) ? location : null;
      const radius = Number(vehicle.radius);
      let area: import("leaflet").Circle | null = null;
      if (online && point) {
        L.circleMarker([point.latitude, point.longitude], { radius: 8, color: "white", weight: 3, fillColor: "#059669", fillOpacity: 1, bubblingMouseEvents: false }).addTo(layer).bindTooltip("Your current GPS");
        if (!active && radius >= 1 && radius <= 200) area = L.circle([point.latitude, point.longitude], { radius: radius * 1000, color: "#059669", weight: 1, fillOpacity: .05, interactive: false }).addTo(layer);
      }
      const visible = active ? [active] : online && point ? liveRadarRequests(requests) : [];
      for (const item of visible) {
        const pickup = { latitude: item.fromLatitude, longitude: item.fromLongitude };
        if (!isRadarPoint(pickup)) continue;
        const label = document.createElement("span"); label.textContent = `${item.passenger.name}: ${item.fromLocation} → ${item.toLocation} · ${item.seats} seats`;
        L.circleMarker([pickup.latitude, pickup.longitude], { radius: selectedId === item.id || active ? 10 : 7, color: "white", weight: 3, fillColor: active ? "#4f46e5" : "#e11d48", fillOpacity: .9, className: styles.pulse, bubblingMouseEvents: false })
          .addTo(layer).bindTooltip(label).on("click", () => { if (!active) { setSelectedId(item.id); setPrice(""); } });
      }
      const viewKey = `${online}:${Boolean(point)}:${radius}:${selectedId}:${active?.id}:${recenter}`;
      if (lastView.current !== viewKey) {
        const focus = active || visible.find(item => item.id === selectedId);
        const pickup = { latitude: focus?.fromLatitude, longitude: focus?.fromLongitude };
        const options = { paddingTopLeft: [24, 100] as [number, number], paddingBottomRight: [24, map.getSize().y * .4] as [number, number], maxZoom: 14 };
        if (isRadarPoint(pickup)) {
          const points: [number, number][] = [[pickup.latitude, pickup.longitude]];
          if (point) points.push([point.latitude, point.longitude]);
          map.fitBounds(L.latLngBounds(points), options);
        } else if (area) map.fitBounds(area.getBounds(), options);
        lastView.current = viewKey;
      }
    }).catch(() => { if (!cancelled) setMessage("Map markers could not load. You can still use the passenger list."); });
    return () => { cancelled = true; layer?.remove(); };
  }, [map, online, location, requests, active, selectedId, vehicle.radius, recenter]);

  async function toggleAvailability() {
    if (toggleLock.current || !ready || actionLock.current || saveVehicleLock.current || (!online && (!vehicleSaved || editingVehicle))) return;
    toggleLock.current = true; tracking.current = false; setBusy(true); setMessage("");
    let writeAttempted = false;
    try {
      let point: RadarPoint | null = null;
      if (!online) {
        validateRadarVehicle(vehicle);
        if (!navigator.geolocation) throw new Error("GPS is not supported by this browser.");
        setMessage("Getting your current GPS…");
        const position = await new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, () => reject(new Error("Current GPS unavailable. Allow location access and try again.")), { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }));
        point = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        if (!isRadarPoint(point)) throw new Error("A valid current GPS location is required.");
      }
      if (!alive.current) return;
      writeAttempted = true;
      const result = await enqueueAvailability(() => availabilityPatch(
          online
            ? {
                available: false,
                action: "stop",
                availabilitySessionToken: availabilitySessionToken.current,
              }
            : { available: true, action: "start", ...point }
        ));
        if (!online && typeof result.availabilitySessionToken === "string") {
          availabilitySessionToken.current = result.availabilitySessionToken;
        } else if (online) {
          availabilitySessionToken.current = null;
        }
      if (!alive.current) return;
      if (result.driverAvailableNow !== !online) throw new Error("Availability could not be confirmed. Refresh status.");
      setOnline(result.driverAvailableNow); locationRef.current = point; setLocation(point);
      lastHeartbeat.current = Date.now(); setRequests([]); setSelectedId(null);
      setMessage(result.driverAvailableNow ? "Online · Looking for nearby passengers." : "Offline · Radar stopped.");
    } catch (cause) {
      report(cause);
      // A failed write may already have reached the server. Re-read before resuming GPS.
      if (writeAttempted) {
        setReady(false);
      }
    } finally { toggleLock.current = false; if (alive.current) setBusy(false); }
  }

  async function act(id: number, payload: object) {
    if (actionLock.current || toggleLock.current || saveVehicleLock.current || uncertain || !ready) return;
    actionLock.current = true; ++actionVersion.current; setActing(true); setMessage("");
    try {
      await readApi(`/api/ride-requests/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const current = await acceptedRequests();
      if (!alive.current) return;
      setAccepted(current); setSelectedId(null); setRequests([]); setOtp(""); setMessage("action" in payload && payload.action === "accept" ? "✅ Booking Confirmed! Passenger ride accepted. Proceed to pickup." : "Ride status updated.");
    } catch (cause) {
      report(cause);
      if (!(cause instanceof RadarApiError) || cause.status >= 500) {
        if (alive.current) { setUncertain(true); setMessage("Response could not be confirmed. Refresh status before another action, or check My Rides."); }
      }
    } finally { ++actionVersion.current; actionLock.current = false; if (alive.current) setActing(false); }
  }
  function accept() {
    if (!selected || !online || !ready || busy || editingVehicle || !vehicleSaved) return;
    try { void act(selected.id, radarAcceptPayload(vehicle, price, locationRef.current)); } catch (cause) { report(cause); }
  }
  const disabled = busy || acting || savingVehicle || uncertain || !ready;
  const fieldClass = "w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs disabled:opacity-60";
  return <div className="col-span-2 max-h-[min(56dvh,480px)] space-y-2 overflow-y-auto pr-1 sm:col-span-4">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-sm font-bold">Find Passengers · Live Radar</h2>
      <button type="button" disabled={busy || acting || savingVehicle} onClick={onClose} className="text-xs font-semibold text-indigo-700 disabled:opacity-50">Close</button>
    </div>
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span role="status" className={`rounded-full px-2 py-1 font-bold ${ready && online ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{!ready ? "Checking status" : online ? "ONLINE" : "OFFLINE"}</span>
      <button type="button" disabled={disabled || (!online && (!vehicleSaved || editingVehicle))} onClick={() => { void toggleAvailability(); }} className="rounded-lg bg-emerald-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50">{busy ? "Updating…" : online ? "Go Offline" : "Go Online"}</button>
      <button type="button" disabled={busy || acting || savingVehicle} onClick={() => { setReady(false); tracking.current = false; setRefresh(n => n + 1); }} className="font-semibold text-indigo-700 disabled:opacity-50">Refresh status</button>
      <button type="button" onClick={() => setRecenter(n => n + 1)} className="font-semibold text-indigo-700">Recenter map</button>
      <Link href="/ride-requests/scheduled" className="text-indigo-700 underline">Scheduled</Link>
    </div>
    {needsLogin && <Link href="/login" className="block text-xs font-semibold text-indigo-700 underline">Log in to find passengers</Link>}
    {message && <p role="status" className="text-xs text-slate-700">{message}</p>}
    {vehicleError && <p role="alert" className="text-xs text-red-700">{vehicleError}</p>}
    {!vehicleLoaded ? <div className="text-xs text-slate-600">
      {vehicleError ? <button type="button" onClick={() => setVehicleRefresh(n => n + 1)} className="font-semibold text-indigo-700 underline">Retry saved vehicle</button> : "Loading saved vehicle…"}
    </div> : editingVehicle ? <div className="space-y-2 rounded-xl border border-slate-200 p-2">
      <p className="text-xs font-semibold">{vehicleSaved ? "Edit vehicle" : "Save your vehicle once"}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <label className="text-[11px] text-slate-600">Vehicle<select id="radar-vehicle-type" disabled={busy || acting || savingVehicle} className={fieldClass} value={vehicleDraft.type} onChange={e => setVehicleDraft(v => ({ ...v, type: e.target.value }))}><option value="Any">Select vehicle</option>{["Car", "Bike", "Scooter", "SUV"].map(type => <option key={type}>{type}</option>)}</select></label>
        <label className="text-[11px] text-slate-600">Model<input id="radar-vehicle-name" disabled={busy || acting || savingVehicle} maxLength={80} placeholder="Maruti Swift" className={fieldClass} value={vehicleDraft.name} onChange={e => setVehicleDraft(v => ({ ...v, name: e.target.value }))} /></label>
        <label className="text-[11px] text-slate-600">Registration<input id="radar-vehicle-number" disabled={busy || acting || savingVehicle} maxLength={30} placeholder="OD 05 AB 1234" className={fieldClass} value={vehicleDraft.number} onChange={e => setVehicleDraft(v => ({ ...v, number: e.target.value.toUpperCase() }))} /></label>
      </div>
      <div className="flex gap-3 text-xs">
        <button type="button" disabled={busy || acting || savingVehicle} onClick={() => { void saveVehicle(); }} className="rounded-lg bg-indigo-700 px-3 py-1.5 font-semibold text-white disabled:opacity-50">{savingVehicle ? "Saving…" : "Save vehicle"}</button>
        {vehicleSaved && <button type="button" disabled={busy || acting || savingVehicle} onClick={() => { setVehicleDraft({ type: vehicle.type, name: vehicle.name, number: vehicle.number }); setEditingVehicle(false); setVehicleError(""); }} className="font-semibold text-slate-600 disabled:opacity-50">Cancel</button>}
      </div>
    </div> : <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2 text-xs">
      <p className="min-w-0 truncate font-semibold" title={`${vehicle.type} · ${vehicle.name} · ${vehicle.number}`}>{vehicle.type} · {vehicle.name} · {vehicle.number}</p>
      <button type="button" disabled={busy || acting || savingVehicle} onClick={() => { setVehicleDraft({ type: vehicle.type, name: vehicle.name, number: vehicle.number }); setEditingVehicle(true); setVehicleError(""); }} className="shrink-0 font-semibold text-indigo-700 disabled:opacity-50">Edit vehicle</button>
    </div>}
    <label className="flex items-center gap-2 text-xs text-slate-600">Radius (km)<input id="radar-radius" disabled={busy || acting || savingVehicle} type="number" min="1" max="200" className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" value={vehicle.radius} onChange={e => setVehicle(v => ({ ...v, radius: e.target.value }))} /></label>
    <p className="text-[11px] text-slate-500">Green: your GPS · Red: passenger pickup. Radar refreshes every 5 seconds while open. Use Go Offline to stop availability.</p>
    {ready && online && !location && <p className="text-xs text-slate-600">Waiting for current GPS. Allow location access.</p>}
    {!active && ready && online && location && <>
      <p className="text-xs text-slate-600">{requests.length ? `${requests.length} nearby requests · Tap a pin or row` : "No nearby Ride Now requests yet."}</p>
      <CompactRideList rides={liveRadarRequests(requests).map(item => ({ id: item.id, name: item.passenger.name, photo: null, from: item.fromLocation, to: item.toLocation, seats: item.seats, dateTime: item.neededOn, latitude: item.fromLatitude ?? null, longitude: item.fromLongitude ?? null }))} onSelect={item => { setSelectedId(item.id); setPrice(""); }} />
    </>}
    {selected && !active && online && location && <div className="space-y-2 rounded-xl border border-rose-100 bg-rose-50 p-2 text-xs">
      <p className="font-semibold">{selected.passenger.name}: {selected.fromLocation} → {selected.toLocation}</p>
      <p>{selected.seats} seats · {selected.preferredVehicle || "Any vehicle"}{selected.match?.pickupDistanceKm != null ? ` · ${selected.match.pickupDistanceKm.toFixed(1)} km to pickup` : ""}</p>
      {selected.contribution != null && <p>Passenger offer: ₹{selected.contribution * selected.seats} total</p>}
      <label className="block">Your total ride price (₹)<input id="radar-price" type="number" min="0" max="1000000" step="any" disabled={disabled} value={price} onChange={e => setPrice(e.target.value)} className={fieldClass} /></label>
      <button type="button" disabled={disabled || editingVehicle || !vehicleSaved} onClick={accept} className="rounded-lg bg-rose-700 px-3 py-2 font-semibold text-white disabled:opacity-50">Accept request{price.trim() && Number.isFinite(Number(price)) ? ` for ₹${Number(price)}` : ""}</button>
    </div>}
    {active && <div className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50 p-2 text-xs">
      <p className="font-semibold">{active.passenger.name}: {active.fromLocation} → {active.toLocation}</p>
      <p>{active.seats} seats · {active.status}{active.paymentAmount != null ? ` · ₹${active.paymentAmount} total` : ""}</p>
      {active.contactUnlocked && active.passenger.mobile && <a href={`tel:${active.passenger.mobile}`} className="block font-semibold text-indigo-700">Call passenger: {active.passenger.mobile}</a>}
      {active.status === "Started" ? <p>Trip in progress</p> : active.otpVerified ? <button type="button" disabled={disabled} onClick={() => { void act(active.id, { action: "start" }); }} className="rounded-lg bg-indigo-700 px-3 py-2 font-semibold text-white disabled:opacity-50">Start trip</button> : active.driverArrivedAt ? <div className="flex gap-2">
        <label className="flex-1">Passenger OTP<input id="radar-otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={4} value={otp} disabled={disabled} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} className={fieldClass} /></label>
        <button type="button" disabled={disabled || !/^\d{4}$/.test(otp)} onClick={() => { if (/^\d{4}$/.test(otp)) void act(active.id, { action: "verify-otp", otp }); }} className="self-end rounded-lg bg-indigo-700 px-3 py-2 font-semibold text-white disabled:opacity-50">Verify OTP</button>
      </div> : <button type="button" disabled={disabled} onClick={() => { void act(active.id, { action: "driver-arrived" }); }} className="rounded-lg bg-indigo-700 px-3 py-2 font-semibold text-white disabled:opacity-50">I Have Arrived</button>}
      {accepted.length > 1 && <p>{accepted.length - 1} more active requests available in My Rides.</p>}
      <Link href="/my-rides" className="block font-semibold text-indigo-700 underline">My Rides</Link>
    </div>}
  </div>;
}
